import * as React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import Emoji from "./emoji";
import { useNavigate } from "react-router-dom";
import AppModal from "./ui/AppModal";

type BaseMessageDialog = {
    open: boolean
    onClose?: () => void
    onConfirm?: () => void;
}

export type PropsMessageDialog = BaseMessageDialog&{
    emoji?: any
    title?: string
    message: React.ReactNode | string
    onConfirm: () => void
    confirmButtonText: string,
    cancelButtonText?: string,
}

export const basicMessageDialog = (message?: string, onConfirm?: () => void, open?: boolean): PropsMessageDialog => {
    return {
        message: message ?? "",
        confirmButtonText: "Ok",
        open: open ?? false,
        onConfirm : onConfirm ?? (() => {}),
    }
}

export const messageErrorConfirmDialog = (onConfirm: () => void): PropsMessageDialog => {
    return {
        title: "Erro!",
        message: "Ocorreu um erro inesperado",
        confirmButtonText: "Ok",
        open: true,
        emoji: "😢",
        onConfirm
    }
}

export const messageSuccessConfirmDialog = (onConfirm: () => void, message: string): PropsMessageDialog => {
    return {
        title: "Sucesso!",
        message: message,
        confirmButtonText: "Ok",
        open: true,
        onConfirm
    }
}

export const MessageDialog: React.FC<PropsMessageDialog> = ({ emoji, open, title, message, confirmButtonText, cancelButtonText, onClose, onConfirm }) => {
    const theme = useTheme();
    return (
        <AppModal
            open={open}
            onClose={onClose ?? onConfirm}
            title={title}
            maxWidth={460}
            footer={
                <>
                    {!!onClose && !!cancelButtonText && (
                        <Button
                            onClick={onClose}
                            sx={{ height: 46, px: 3, color: "secondary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.divider}` }}
                        >
                            {cancelButtonText}
                        </Button>
                    )}
                    <Button variant="contained" onClick={onConfirm} sx={{ height: 46, px: 3 }}>
                        {confirmButtonText}
                    </Button>
                </>
            }
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                {!!emoji && (
                    <Box sx={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>
                        <Emoji symbol={emoji} />
                    </Box>
                )}
                <Typography sx={{ fontSize: "14.5px", color: "text.secondary", lineHeight: 1.6 }}>{message}</Typography>
            </Box>
        </AppModal>
    )
}

export const MessagePayedResourceDialog: React.FC<BaseMessageDialog> = ({ open, onClose, onConfirm }) => {
    const navigate = useNavigate()

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm()
            return
        }
        navigate('/dashboard/pricing')
        onClose?.()
    }

    return (
        <MessageDialog
            open={open}
            onClose={onClose}
            onConfirm={handleConfirm}
            confirmButtonText="Fazer agora"
            cancelButtonText="Fazer depois"
            title="Você acabou de encontrar um recurso pago"
            message="Deseja fazer um upgrade de plano para continuar?"
        />
    )
}

export const MessageLockedResourceDialog: React.FC<BaseMessageDialog> = ({ open, onClose }) => {
    const handleConfirm = () => {
        onClose?.()
    }
    return (
        <MessageDialog
            open={open}
            onConfirm={handleConfirm}
            confirmButtonText="Ok"
            title="Sua assinatura ainda está em verificação."
            message="Em breve este recurso estará disponível."
        />
    )
}

export const NoPatientDialog: React.FC<BaseMessageDialog> = ({ open, onClose, onConfirm }) => {
    const handleConfirm = () => {
        onClose?.()
        onConfirm?.()
    }
    return (
        <MessageDialog
            open={open}
            onConfirm={handleConfirm}
            onClose={onClose}
            confirmButtonText="Sim"
            cancelButtonText="Não"
            title="Nenhum paciente selecionado"
            message={
                    <>
                        Essa consulta <strong>não</strong> será salva no histórico de consultas pois <strong>não</strong> está atrelada a um paciente. 
                        Você pode salvar o PDF para guardá-la, deseja continuar mesmo assim?
                    </>
            }
        />
    );
};
